import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  useCallback,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useDate } from './date-context';
import type { BtcApplication, Expense, Income, Budget, Tag, Caixinha, CaixinhaSnapshot, CaixinhaLog, Conversion, CorporateAction, Earning, InvestmentAsset, Trade } from '../types';
import { defaultBudgetProportions, PUBLIC_TAGS } from '../types';
import * as firestoreService from '../services/firestore';
import { useBitcoinPrice } from '../hooks/use-bitcoin-price';
import { fetchQuotes } from '../lib/quotes';
import {
  computePositions,
  computeSummary,
  totalEarningsReceived,
} from '../lib/investments';
import { getCaixinhasTotal, getTotalBtc } from '../lib/caixinhas';
import type { PortfolioSummary, Position } from '../lib/investments';

interface AppDataContextType {
  // Budget
  budget: Budget;
  originalBudget: Budget | null;
  loadingBudget: boolean;
  saveBudget: (budgetData: Omit<Budget, 'id' | 'userId'>) => Promise<void>;

  // Expenses
  expenses: Expense[];
  allExpenses: Expense[];
  loadingExpenses: boolean;
  addExpense: (
    expenseData: Omit<Expense, 'id' | 'createdAt' | 'userId'>
  ) => Promise<void>;
  updateExpense: (id: string, expenseData: Partial<Expense>) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;

  // Incomes
  incomes: Income[];
  loadingIncomes: boolean;
  addIncome: (
    incomeData: Omit<Income, 'id' | 'createdAt' | 'userId'>
  ) => Promise<void>;
  updateIncome: (id: string, incomeData: Partial<Income>) => Promise<void>;
  deleteIncome: (id: string) => Promise<void>;

  // Tags
  tags: Tag[];
  loadingTags: boolean;
  addTag: (tagData: Omit<Tag, 'id' | 'createdAt'>) => Promise<void>;
  updateTag: (id: string, tagData: Partial<Tag>) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;

  // BTC Applications
  btcApplications: BtcApplication[];
  loadingBtcApplications: boolean;
  addBtcApplication: (data: Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>) => Promise<void>;
  updateBtcApplication: (id: string, data: Partial<Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>>) => Promise<void>;
  deleteBtcApplication: (id: string) => Promise<void>;

  // Caixinhas
  caixinhas: Caixinha[];
  loadingCaixinhas: boolean;
  addCaixinha: (data: { title: string; description?: string }) => Promise<void>;
  updateCaixinha: (id: string, data: Partial<Pick<Caixinha, 'title' | 'description'>>) => Promise<void>;
  deleteCaixinha: (id: string) => Promise<void>;
  addSnapshot: (caixinhaId: string, data: { date: Date; value: number }) => Promise<void>;
  updateSnapshot: (caixinhaId: string, snapshotId: string, data: Partial<Pick<CaixinhaSnapshot, 'date' | 'value'>>) => Promise<void>;
  deleteSnapshot: (caixinhaId: string, snapshotId: string) => Promise<void>;
  addCaixinhaLog: (caixinhaId: string, data: { date: Date; value: number; description: string }) => Promise<void>;
  updateCaixinhaLog: (caixinhaId: string, logId: string, data: Partial<Pick<CaixinhaLog, 'date' | 'value' | 'description'>>) => Promise<void>;
  deleteCaixinhaLog: (caixinhaId: string, logId: string) => Promise<void>;

  // Investments (sempre por usuário — IRPF é individual)
  investmentAssets: InvestmentAsset[];
  activeInvestmentAssets: InvestmentAsset[];
  trades: Trade[];
  earnings: Earning[];
  corporateActions: CorporateAction[];
  conversions: Conversion[];
  loadingInvestments: boolean;
  addInvestmentAsset: (
    data: Omit<InvestmentAsset, 'id' | 'userId' | 'createdAt'>
  ) => Promise<void>;
  updateInvestmentAsset: (
    id: string,
    data: Partial<Omit<InvestmentAsset, 'id' | 'userId' | 'createdAt'>>
  ) => Promise<void>;
  deleteInvestmentAsset: (id: string) => Promise<void>;
  addTrade: (data: Omit<Trade, 'id' | 'userId' | 'createdAt'>) => Promise<void>;
  updateTrade: (
    id: string,
    data: Partial<Omit<Trade, 'id' | 'userId' | 'createdAt'>>
  ) => Promise<void>;
  deleteTrade: (id: string) => Promise<void>;
  addEarning: (data: Omit<Earning, 'id' | 'userId' | 'createdAt'>) => Promise<void>;
  updateEarning: (
    id: string,
    data: Partial<Omit<Earning, 'id' | 'userId' | 'createdAt'>>
  ) => Promise<void>;
  deleteEarning: (id: string) => Promise<void>;
  addCorporateAction: (
    data: Omit<CorporateAction, 'id' | 'userId' | 'createdAt'>
  ) => Promise<void>;
  deleteCorporateAction: (id: string) => Promise<void>;
  addConversion: (
    data: Omit<Conversion, 'id' | 'userId' | 'createdAt'>
  ) => Promise<void>;
  deleteConversion: (id: string) => Promise<void>;
  /**
   * Operações e proventos cujo papel foi excluído antes da cascata existir.
   * Eles voltam no reload e atrapalham a deduplicação da importação.
   */
  orphanRecords: { trades: Trade[]; earnings: Earning[] };
  cleanupOrphanRecords: () => Promise<number>;

  /**
   * Proventos cujo tipo não bate com o tipo do papel: dividendo num FII ou
   * rendimento de FII numa ação. Mesma isenção, mas ficha diferente no IRPF.
   */
  mismatchedEarnings: Earning[];
  fixEarningKinds: () => Promise<number>;

  /** Grava de uma vez os papéis, operações e proventos vindos do relatório da B3. */
  importInvestments: (
    input: Omit<firestoreService.ImportInvestmentsInput, 'existingTickers'>
  ) => Promise<{ assets: number; trades: number; earnings: number }>;

  /** Busca cotações na brapi e persiste em currentPrice. */
  refreshQuotes: () => Promise<void>;
  refreshingQuotes: boolean;
  quotesError: string | null;
  positions: Position[];
  portfolioSummary: PortfolioSummary;

  /** Proventos líquidos creditados no mês selecionado. */
  monthEarnings: number;

  // Patrimônio: reservas + bitcoin + carteira de investimentos
  caixinhasTotal: number;
  bitcoinTotal: number;
  investmentsTotal: number;
  netWorth: number;

  // Computed values
  totalIncome: number;
  totalMoney: number;
  totalExpenses: number;
  balance: number;

  // Bitcoin price
  bitcoinPrice: { usd: number; brl: number } | null;
  loadingBitcoinPrice: boolean;
  bitcoinPriceError: boolean;

  // Refresh data
  refreshData: () => Promise<void>;

  // Account switching (admin only)
  viewingAs: { uid: string; email: string; displayName: string } | null;
  setViewingAs: (v: { uid: string; email: string; displayName: string } | null) => void;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

interface AppDataProviderProps {
  children: ReactNode;
}

export function AppDataProvider({ children }: AppDataProviderProps) {
  const { month, year } = useDate();

  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [viewingAs, setViewingAs] = useState<{ uid: string; email: string; displayName: string } | null>(null);

  // State
  const [budget, setBudget] = useState<Budget | null>(null);
  const [loadingBudget, setLoadingBudget] = useState(false);

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  const [incomes, setIncomes] = useState<Income[]>([]);
  const [loadingIncomes, setLoadingIncomes] = useState(false);

  const [tags, setTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const [caixinhas, setCaixinhas] = useState<Caixinha[]>([]);
  const [loadingCaixinhas, setLoadingCaixinhas] = useState(false);

  const [btcApplications, setBtcApplications] = useState<BtcApplication[]>([]);
  const [loadingBtcApplications, setLoadingBtcApplications] = useState(false);

  const [investmentAssets, setInvestmentAssets] = useState<InvestmentAsset[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [earnings, setEarnings] = useState<Earning[]>([]);
  const [corporateActions, setCorporateActions] = useState<CorporateAction[]>([]);
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);
  const [refreshingQuotes, setRefreshingQuotes] = useState(false);
  const [quotesError, setQuotesError] = useState<string | null>(null);

  const { price: bitcoinPrice, loading: loadingBitcoinPrice, error: bitcoinPriceError } = useBitcoinPrice();

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      if (!currentUser) setViewingAs(null);
    });
    return unsubscribe;
  }, []);

  const effectiveUserId = viewingAs?.uid ?? user?.uid ?? null;

  // Computed values
  const totalIncome = incomes.reduce((sum, income) => sum + income.value, 0);

  // Dividendos, JCP e rendimentos de FII creditados no mês, líquidos de IRRF.
  // Substituem os antigos recebimentos de ativos, que eram o mesmo dinheiro
  // lançado à mão — somar os dois contaria a entrada duas vezes.
  const monthEarnings = useMemo(
    () => totalEarningsReceived(earnings, year, month),
    [earnings, year, month]
  );

  const totalMoney = totalIncome + monthEarnings;
  const totalExpenses = expenses.reduce(
    (sum, expense) => sum + expense.value,
    0
  );
  const balance = totalMoney - totalExpenses;

  // Effective budget: use saved budget or create default based on income
  const effectiveBudget: Budget = budget
    ? budget
    : {
        id: 'default',
        userId: 'default',
        ...defaultBudgetProportions,
        month,
        year,
      };

  // Load data when month/year changes or user changes
  const loadData = useCallback(async () => {
    if (authLoading) return;

    // Load budget
    setLoadingBudget(true);
    try {
      if (effectiveUserId) {
        const budgetData = await firestoreService.getBudget(effectiveUserId, month, year);
        setBudget(budgetData);
      } else {
        setBudget(null);
      }
    } catch (error) {
      console.error('Error loading budget:', error);
      setBudget(null);
    } finally {
      setLoadingBudget(false);
    }

    // Load expenses
    setLoadingExpenses(true);
    try {
      if (effectiveUserId) {
        const expensesData = await firestoreService.getExpenses(effectiveUserId, month, year);
        setExpenses(expensesData);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
      setExpenses([]);
    } finally {
      setLoadingExpenses(false);
    }

    // Load incomes
    setLoadingIncomes(true);
    try {
      if (effectiveUserId) {
        const incomesData = await firestoreService.getIncomes(effectiveUserId, month, year);
        setIncomes(incomesData);
      } else {
        setIncomes([]);
      }
    } catch (error) {
      console.error('Error loading incomes:', error);
      setIncomes([]);
    } finally {
      setLoadingIncomes(false);
    }

    // Load user tags
    setLoadingTags(true);
    try {
      if (effectiveUserId) {
        const tagsData = await firestoreService.getTags(effectiveUserId);
        setTags(tagsData);
      } else {
        setTags([]);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    } finally {
      setLoadingTags(false);
    }

    // Load caixinhas
    setLoadingCaixinhas(true);
    try {
      if (effectiveUserId) {
        const data = await firestoreService.getCaixinhas(effectiveUserId);
        setCaixinhas(data);
      } else {
        setCaixinhas([]);
      }
    } catch (error) {
      console.error('Error loading caixinhas:', error);
      setCaixinhas([]);
    } finally {
      setLoadingCaixinhas(false);
    }

    // Load BTC applications
    setLoadingBtcApplications(true);
    try {
      if (effectiveUserId) {
        const data = await firestoreService.getBtcApplications(effectiveUserId);
        setBtcApplications(data);
      } else {
        setBtcApplications([]);
      }
    } catch (error) {
      console.error('Error loading btc applications:', error);
      setBtcApplications([]);
    } finally {
      setLoadingBtcApplications(false);
    }

    // Load investments (histórico completo — a apuração do IRPF precisa de
    // todos os anos para o custo médio e o prejuízo acumulado)
    setLoadingInvestments(true);
    try {
      if (effectiveUserId) {
        const [assetsList, tradesList, earningsList, actionsList, conversionsList] =
          await Promise.all([
            firestoreService.getInvestmentAssets(effectiveUserId),
            firestoreService.getTrades(effectiveUserId),
            firestoreService.getEarnings(effectiveUserId),
            firestoreService.getCorporateActions(effectiveUserId),
            firestoreService.getConversions(effectiveUserId),
          ]);
        setInvestmentAssets(assetsList);
        setTrades(tradesList);
        setEarnings(earningsList);
        setCorporateActions(actionsList);
        setConversions(conversionsList);
      } else {
        setInvestmentAssets([]);
        setTrades([]);
        setEarnings([]);
        setCorporateActions([]);
        setConversions([]);
      }
    } catch (error) {
      console.error('Error loading investments:', error);
      setInvestmentAssets([]);
      setTrades([]);
      setEarnings([]);
      setCorporateActions([]);
      setConversions([]);
    } finally {
      setLoadingInvestments(false);
    }

  }, [month, year, effectiveUserId, authLoading]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load all expenses once per user (not month-bound)
  useEffect(() => {
    if (!effectiveUserId) { setAllExpenses([]); return; }
    firestoreService.getAllExpenses(effectiveUserId)
      .then(setAllExpenses)
      .catch(() => setAllExpenses([]));
  }, [effectiveUserId]);

  // Budget operations
  const saveBudget = async (budgetData: Omit<Budget, 'id' | 'userId'>) => {
    try {
      if (effectiveUserId) {
        const savedBudget = await firestoreService.saveBudget(
          effectiveUserId,
          budgetData
        );
        setBudget(savedBudget);
      } else {
        // Local state only when not logged in
        setBudget({
          id: 'local',
          userId: 'local',
          ...budgetData,
        });
      }
    } catch (error) {
      console.error('Error saving budget:', error);
      throw error;
    }
  };

  // Expense operations
  const addExpense = async (
    expenseData: Omit<Expense, 'id' | 'createdAt' | 'userId'>
  ) => {
    try {
      let newExpense: Expense;
      if (effectiveUserId) {
        newExpense = await firestoreService.addExpense(
          effectiveUserId,
          expenseData
        );
      } else {
        newExpense = {
          id: `expense-${Date.now()}`,
          createdAt: new Date(),
          userId: 'local',
          ...expenseData,
        };
      }

      // `expenses` holds only the selected month — a gasto dated outside it
      // (repetido, importado) belongs to another month's list.
      const date = new Date(newExpense.date);
      if (date.getMonth() + 1 === month && date.getFullYear() === year) {
        setExpenses((prev) => [newExpense, ...prev]);
      }
      setAllExpenses((prev) => [newExpense, ...prev]);
    } catch (error) {
      console.error('Error adding expense:', error);
      throw error;
    }
  };

  const updateExpense = async (id: string, expenseData: Partial<Expense>) => {
    try {
      if (user) {
        await firestoreService.updateExpense(id, expenseData);
      }
      // Update local state
      const updater = (prev: Expense[]) =>
        prev.map((expense) => expense.id === id ? { ...expense, ...expenseData } : expense);
      setExpenses(updater);
      setAllExpenses(updater);
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  };

  const deleteExpense = async (id: string) => {
    try {
      if (user) {
        await firestoreService.deleteExpense(id);
      }
      // Update local state
      const filter = (prev: Expense[]) => prev.filter((expense) => expense.id !== id);
      setExpenses(filter);
      setAllExpenses(filter);
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  };

  // Income operations
  const addIncome = async (
    incomeData: Omit<Income, 'id' | 'createdAt' | 'userId'>
  ) => {
    try {
      if (effectiveUserId) {
        const newIncome = await firestoreService.addIncome(
          effectiveUserId,
          incomeData
        );
        setIncomes((prev) => [newIncome, ...prev]);
      } else {
        // Local state only when not logged in
        const newIncome: Income = {
          id: `income-${Date.now()}`,
          createdAt: new Date(),
          userId: 'local',
          ...incomeData,
        };
        setIncomes((prev) => [newIncome, ...prev]);
      }
    } catch (error) {
      console.error('Error adding income:', error);
      throw error;
    }
  };

  const updateIncome = async (id: string, incomeData: Partial<Income>) => {
    try {
      if (user) {
        await firestoreService.updateIncome(id, incomeData);
      }
      // Update local state
      setIncomes((prev) =>
        prev.map((income) =>
          income.id === id ? { ...income, ...incomeData } : income
        )
      );
    } catch (error) {
      console.error('Error updating income:', error);
      throw error;
    }
  };

  const deleteIncome = async (id: string) => {
    try {
      if (user) {
        await firestoreService.deleteIncome(id);
      }
      // Update local state
      setIncomes((prev) => prev.filter((income) => income.id !== id));
    } catch (error) {
      console.error('Error deleting income:', error);
      throw error;
    }
  };

  const addTag = async (tagData: Omit<Tag, 'id' | 'createdAt'>) => {
    try {
      if (!effectiveUserId) return;
      const newTag = await firestoreService.addTag(effectiveUserId, { name: tagData.name, icon: tagData.icon });
      setTags((prev) => [...prev, newTag]);
    } catch (error) {
      console.error('Error adding tag:', error);
      throw error;
    }
  };

  const updateTag = async (id: string, tagData: Partial<Tag>) => {
    try {
      await firestoreService.updateTag(id, tagData);
      setTags((prev) => prev.map((t) => (t.id === id ? { ...t, ...tagData } : t)));
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
  };

  const deleteTag = async (id: string) => {
    try {
      await firestoreService.deleteTag(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  };

  // BTC Application operations
  const addBtcApplication = async (data: Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>) => {
    if (!effectiveUserId) return;
    try {
      const newApp = await firestoreService.addBtcApplication(effectiveUserId, data);
      setBtcApplications((prev) => [...prev, newApp].sort((a, b) => a.date.getTime() - b.date.getTime()));
    } catch (error) {
      console.error('Error adding btc application:', error);
      throw error;
    }
  };

  const updateBtcApplication = async (id: string, data: Partial<Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>>) => {
    try {
      await firestoreService.updateBtcApplication(id, data);
      setBtcApplications((prev) => prev.map((a) => (a.id === id ? { ...a, ...data } : a)));
    } catch (error) {
      console.error('Error updating btc application:', error);
      throw error;
    }
  };

  const deleteBtcApplication = async (id: string) => {
    try {
      await firestoreService.deleteBtcApplication(id);
      setBtcApplications((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error('Error deleting btc application:', error);
      throw error;
    }
  };

  // Caixinha operations
  const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

  const addCaixinha = async (data: { title: string; description?: string }) => {
    try {
      if (!effectiveUserId) return;
      const newCaixinha = await firestoreService.addCaixinha(effectiveUserId, data);
      setCaixinhas((prev) => [...prev, newCaixinha]);
    } catch (error) {
      console.error('Error adding caixinha:', error);
      throw error;
    }
  };

  const updateCaixinha = async (id: string, data: Partial<Pick<Caixinha, 'title' | 'description'>>) => {
    try {
      await firestoreService.updateCaixinha(id, data);
      setCaixinhas((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
    } catch (error) {
      console.error('Error updating caixinha:', error);
      throw error;
    }
  };

  const deleteCaixinha = async (id: string) => {
    try {
      await firestoreService.deleteCaixinha(id);
      setCaixinhas((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Error deleting caixinha:', error);
      throw error;
    }
  };

  const addSnapshot = async (caixinhaId: string, data: { date: Date; value: number }) => {
    const newSnapshot: CaixinhaSnapshot = { id: genId(), ...data };
    const caixinha = caixinhas.find((c) => c.id === caixinhaId);
    if (!caixinha) return;
    const updated = [...caixinha.snapshots, newSnapshot];
    try {
      await firestoreService.updateCaixinha(caixinhaId, { snapshots: updated });
      setCaixinhas((prev) =>
        prev.map((c) => (c.id === caixinhaId ? { ...c, snapshots: updated } : c))
      );
    } catch (error) {
      console.error('Error adding snapshot:', error);
      throw error;
    }
  };

  const updateSnapshot = async (caixinhaId: string, snapshotId: string, data: Partial<Pick<CaixinhaSnapshot, 'date' | 'value'>>) => {
    const caixinha = caixinhas.find((c) => c.id === caixinhaId);
    if (!caixinha) return;
    const updated = caixinha.snapshots.map((s) => (s.id === snapshotId ? { ...s, ...data } : s));
    try {
      await firestoreService.updateCaixinha(caixinhaId, { snapshots: updated });
      setCaixinhas((prev) =>
        prev.map((c) => (c.id === caixinhaId ? { ...c, snapshots: updated } : c))
      );
    } catch (error) {
      console.error('Error updating snapshot:', error);
      throw error;
    }
  };

  const deleteSnapshot = async (caixinhaId: string, snapshotId: string) => {
    const caixinha = caixinhas.find((c) => c.id === caixinhaId);
    if (!caixinha) return;
    const updated = caixinha.snapshots.filter((s) => s.id !== snapshotId);
    try {
      await firestoreService.updateCaixinha(caixinhaId, { snapshots: updated });
      setCaixinhas((prev) =>
        prev.map((c) => (c.id === caixinhaId ? { ...c, snapshots: updated } : c))
      );
    } catch (error) {
      console.error('Error deleting snapshot:', error);
      throw error;
    }
  };

  const addCaixinhaLog = async (caixinhaId: string, data: { date: Date; value: number; description: string }) => {
    const newLog: CaixinhaLog = { id: genId(), ...data };
    const caixinha = caixinhas.find((c) => c.id === caixinhaId);
    if (!caixinha) return;
    const updated = [...caixinha.log, newLog];
    try {
      await firestoreService.updateCaixinha(caixinhaId, { log: updated });
      setCaixinhas((prev) =>
        prev.map((c) => (c.id === caixinhaId ? { ...c, log: updated } : c))
      );
    } catch (error) {
      console.error('Error adding log:', error);
      throw error;
    }
  };

  const updateCaixinhaLog = async (caixinhaId: string, logId: string, data: Partial<Pick<CaixinhaLog, 'date' | 'value' | 'description'>>) => {
    const caixinha = caixinhas.find((c) => c.id === caixinhaId);
    if (!caixinha) return;
    const updated = caixinha.log.map((l) => (l.id === logId ? { ...l, ...data } : l));
    try {
      await firestoreService.updateCaixinha(caixinhaId, { log: updated });
      setCaixinhas((prev) =>
        prev.map((c) => (c.id === caixinhaId ? { ...c, log: updated } : c))
      );
    } catch (error) {
      console.error('Error updating log:', error);
      throw error;
    }
  };

  const deleteCaixinhaLog = async (caixinhaId: string, logId: string) => {
    const caixinha = caixinhas.find((c) => c.id === caixinhaId);
    if (!caixinha) return;
    const updated = caixinha.log.filter((l) => l.id !== logId);
    try {
      await firestoreService.updateCaixinha(caixinhaId, { log: updated });
      setCaixinhas((prev) =>
        prev.map((c) => (c.id === caixinhaId ? { ...c, log: updated } : c))
      );
    } catch (error) {
      console.error('Error deleting log:', error);
      throw error;
    }
  };

  // ============================================
  // INVESTMENTS
  // ============================================

  const activeInvestmentAssets = investmentAssets.filter((a) => !a.deactivated);

  const positions = useMemo(
    () =>
      computePositions({
        assets: investmentAssets,
        trades,
        earnings,
        corporateActions,
        conversions,
      }),
    [investmentAssets, trades, earnings, corporateActions, conversions]
  );

  const portfolioSummary = useMemo(() => computeSummary(positions), [positions]);

  // As caixinhas seguem o mês selecionado; a carteira usa a cotação de agora,
  // que é a única que a API devolve.
  const caixinhasTotal = useMemo(
    () => getCaixinhasTotal(caixinhas, new Date(year, month, 0, 23, 59, 59, 999)),
    [caixinhas, month, year]
  );

  const bitcoinTotal = useMemo(
    () => (bitcoinPrice ? getTotalBtc(btcApplications) * bitcoinPrice.brl : 0),
    [btcApplications, bitcoinPrice]
  );

  const investmentsTotal = portfolioSummary.marketValue;
  const netWorth = caixinhasTotal + bitcoinTotal + investmentsTotal;

  const orphanRecords = useMemo(() => {
    const assetIds = new Set(investmentAssets.map((a) => a.id));
    return {
      trades: trades.filter((t) => !assetIds.has(t.assetId)),
      earnings: earnings.filter((e) => !assetIds.has(e.assetId)),
    };
  }, [investmentAssets, trades, earnings]);

  /** Qual seria o tipo certo para este provento, dado o tipo do papel. */
  const correctEarningKind = useCallback(
    (earning: Earning): Earning['kind'] | null => {
      const asset = investmentAssets.find((a) => a.id === earning.assetId);
      if (!asset) return null;
      if (asset.type === 'fii' && earning.kind === 'dividendo') return 'rendimento_fii';
      if (asset.type === 'acao' && earning.kind === 'rendimento_fii') return 'dividendo';
      return null;
    },
    [investmentAssets]
  );

  const mismatchedEarnings = useMemo(
    () => earnings.filter((e) => correctEarningKind(e) !== null),
    [earnings, correctEarningKind]
  );

  const fixEarningKinds = async () => {
    const updates = mismatchedEarnings.flatMap((earning) => {
      const kind = correctEarningKind(earning);
      return kind ? [{ id: earning.id, kind }] : [];
    });
    if (updates.length === 0) return 0;

    try {
      await firestoreService.updateEarningKinds(updates);
      const byId = new Map(updates.map((u) => [u.id, u.kind]));
      setEarnings((prev) =>
        prev.map((e) => (byId.has(e.id) ? { ...e, kind: byId.get(e.id)! } : e))
      );
      return updates.length;
    } catch (error) {
      console.error('Error fixing earning kinds:', error);
      throw error;
    }
  };

  const cleanupOrphanRecords = async () => {
    if (orphanRecords.trades.length === 0 && orphanRecords.earnings.length === 0) {
      return 0;
    }
    try {
      const removed = await firestoreService.deleteOrphanInvestmentRecords(orphanRecords);
      const tradeIds = new Set(orphanRecords.trades.map((t) => t.id));
      const earningIds = new Set(orphanRecords.earnings.map((e) => e.id));
      setTrades((prev) => prev.filter((t) => !tradeIds.has(t.id)));
      setEarnings((prev) => prev.filter((e) => !earningIds.has(e.id)));
      return removed;
    } catch (error) {
      console.error('Error cleaning orphan records:', error);
      throw error;
    }
  };

  const addInvestmentAsset = async (
    data: Omit<InvestmentAsset, 'id' | 'userId' | 'createdAt'>
  ) => {
    if (!effectiveUserId) return;
    try {
      const created = await firestoreService.addInvestmentAsset(effectiveUserId, data);
      setInvestmentAssets((prev) =>
        [...prev, created].sort((a, b) => a.ticker.localeCompare(b.ticker))
      );
    } catch (error) {
      console.error('Error adding investment asset:', error);
      throw error;
    }
  };

  const updateInvestmentAsset = async (
    id: string,
    data: Partial<Omit<InvestmentAsset, 'id' | 'userId' | 'createdAt'>>
  ) => {
    try {
      await firestoreService.updateInvestmentAsset(id, data);
      setInvestmentAssets((prev) =>
        prev
          .map((a) => (a.id === id ? { ...a, ...data } : a))
          .sort((a, b) => a.ticker.localeCompare(b.ticker))
      );
    } catch (error) {
      console.error('Error updating investment asset:', error);
      throw error;
    }
  };

  const deleteInvestmentAsset = async (id: string) => {
    try {
      await firestoreService.deleteInvestmentAsset(id);
      setInvestmentAssets((prev) => prev.filter((a) => a.id !== id));
      setTrades((prev) => prev.filter((t) => t.assetId !== id));
      setEarnings((prev) => prev.filter((e) => e.assetId !== id));
      setCorporateActions((prev) => prev.filter((c) => c.assetId !== id));
      setConversions((prev) =>
        prev.filter((c) => c.fromAssetId !== id && c.toAssetId !== id)
      );
    } catch (error) {
      console.error('Error deleting investment asset:', error);
      throw error;
    }
  };

  const byDateAsc = <T extends { date: Date }>(list: T[]) =>
    [...list].sort((a, b) => a.date.getTime() - b.date.getTime());

  const addTrade = async (data: Omit<Trade, 'id' | 'userId' | 'createdAt'>) => {
    if (!effectiveUserId) return;
    try {
      const created = await firestoreService.addTrade(effectiveUserId, data);
      setTrades((prev) => byDateAsc([...prev, created]));
    } catch (error) {
      console.error('Error adding trade:', error);
      throw error;
    }
  };

  const updateTrade = async (
    id: string,
    data: Partial<Omit<Trade, 'id' | 'userId' | 'createdAt'>>
  ) => {
    try {
      await firestoreService.updateTrade(id, data);
      setTrades((prev) => byDateAsc(prev.map((t) => (t.id === id ? { ...t, ...data } : t))));
    } catch (error) {
      console.error('Error updating trade:', error);
      throw error;
    }
  };

  const deleteTrade = async (id: string) => {
    try {
      await firestoreService.deleteTrade(id);
      setTrades((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      console.error('Error deleting trade:', error);
      throw error;
    }
  };

  const addEarning = async (data: Omit<Earning, 'id' | 'userId' | 'createdAt'>) => {
    if (!effectiveUserId) return;
    try {
      const created = await firestoreService.addEarning(effectiveUserId, data);
      setEarnings((prev) =>
        [...prev, created].sort((a, b) => a.paymentDate.getTime() - b.paymentDate.getTime())
      );
    } catch (error) {
      console.error('Error adding earning:', error);
      throw error;
    }
  };

  const updateEarning = async (
    id: string,
    data: Partial<Omit<Earning, 'id' | 'userId' | 'createdAt'>>
  ) => {
    try {
      await firestoreService.updateEarning(id, data);
      setEarnings((prev) =>
        prev
          .map((e) => (e.id === id ? { ...e, ...data } : e))
          .sort((a, b) => a.paymentDate.getTime() - b.paymentDate.getTime())
      );
    } catch (error) {
      console.error('Error updating earning:', error);
      throw error;
    }
  };

  const deleteEarning = async (id: string) => {
    try {
      await firestoreService.deleteEarning(id);
      setEarnings((prev) => prev.filter((e) => e.id !== id));
    } catch (error) {
      console.error('Error deleting earning:', error);
      throw error;
    }
  };

  const addCorporateAction = async (
    data: Omit<CorporateAction, 'id' | 'userId' | 'createdAt'>
  ) => {
    if (!effectiveUserId) return;
    try {
      const created = await firestoreService.addCorporateAction(effectiveUserId, data);
      setCorporateActions((prev) => byDateAsc([...prev, created]));
    } catch (error) {
      console.error('Error adding corporate action:', error);
      throw error;
    }
  };

  const deleteCorporateAction = async (id: string) => {
    try {
      await firestoreService.deleteCorporateAction(id);
      setCorporateActions((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Error deleting corporate action:', error);
      throw error;
    }
  };

  const importInvestments = async (
    input: Omit<firestoreService.ImportInvestmentsInput, 'existingTickers'>
  ) => {
    if (!effectiveUserId) return { assets: 0, trades: 0, earnings: 0 };

    const existingTickers: Record<string, string> = {};
    for (const asset of investmentAssets) {
      existingTickers[asset.ticker.toUpperCase()] = asset.id;
    }

    try {
      const created = await firestoreService.importInvestments(effectiveUserId, {
        ...input,
        existingTickers,
      });

      setInvestmentAssets((prev) =>
        [...prev, ...created.assets].sort((a, b) => a.ticker.localeCompare(b.ticker))
      );
      setTrades((prev) =>
        [...prev, ...created.trades].sort((a, b) => a.date.getTime() - b.date.getTime())
      );
      setEarnings((prev) =>
        [...prev, ...created.earnings].sort(
          (a, b) => a.paymentDate.getTime() - b.paymentDate.getTime()
        )
      );

      return {
        assets: created.assets.length,
        trades: created.trades.length,
        earnings: created.earnings.length,
      };
    } catch (error) {
      console.error('Error importing investments:', error);
      throw error;
    }
  };

  const addConversion = async (
    data: Omit<Conversion, 'id' | 'userId' | 'createdAt'>
  ) => {
    if (!effectiveUserId) return;
    try {
      const created = await firestoreService.addConversion(effectiveUserId, data);
      setConversions((prev) => byDateAsc([...prev, created]));
    } catch (error) {
      console.error('Error adding conversion:', error);
      throw error;
    }
  };

  const deleteConversion = async (id: string) => {
    try {
      await firestoreService.deleteConversion(id);
      setConversions((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Error deleting conversion:', error);
      throw error;
    }
  };

  const refreshQuotes = useCallback(async () => {
    const openTickers = new Set(positions.map((p) => p.asset.ticker));
    const tickers = investmentAssets
      .filter((a) => !a.deactivated && a.type !== 'tesouro' && openTickers.has(a.ticker))
      .map((a) => a.ticker);
    if (tickers.length === 0) return;

    setRefreshingQuotes(true);
    setQuotesError(null);
    try {
      const quotes = await fetchQuotes(tickers);
      const updates = investmentAssets.flatMap((asset) => {
        const quote = quotes.get(asset.ticker.toUpperCase());
        if (!quote) return [];
        return [{ id: asset.id, currentPrice: quote.price }];
      });

      if (updates.length === 0) {
        setQuotesError('Nenhuma cotação encontrada para os tickers cadastrados.');
        return;
      }

      await firestoreService.updateInvestmentPrices(updates);

      const now = new Date();
      const priceById = new Map(updates.map((u) => [u.id, u.currentPrice]));
      setInvestmentAssets((prev) =>
        prev.map((asset) =>
          priceById.has(asset.id)
            ? { ...asset, currentPrice: priceById.get(asset.id)!, priceUpdatedAt: now }
            : asset
        )
      );

      // Avisa quando algum ticker não voltou da API, sem bloquear o resto.
      const missing = tickers.filter((t) => !quotes.has(t.toUpperCase()));
      if (missing.length > 0) {
        setQuotesError(`Sem cotação para: ${missing.join(', ')}`);
      }
    } catch (error) {
      console.error('Error refreshing quotes:', error);
      setQuotesError(
        error instanceof Error ? error.message : 'Falha ao buscar cotações.'
      );
    } finally {
      setRefreshingQuotes(false);
    }
  }, [investmentAssets, positions]);

  const refreshData = useCallback(async () => {
    await loadData();
  }, [loadData]);

  return (
    <AppDataContext.Provider
      value={{
        budget: effectiveBudget,
        originalBudget: budget,
        loadingBudget,
        saveBudget,
        expenses,
        allExpenses,
        loadingExpenses,
        addExpense,
        updateExpense,
        deleteExpense,
        incomes,
        loadingIncomes,
        addIncome,
        updateIncome,
        deleteIncome,
        tags: [...PUBLIC_TAGS, ...tags],
        loadingTags,
        addTag,
        updateTag,
        deleteTag,
        btcApplications,
        loadingBtcApplications,
        addBtcApplication,
        updateBtcApplication,
        deleteBtcApplication,
        caixinhas,
        loadingCaixinhas,
        addCaixinha,
        updateCaixinha,
        deleteCaixinha,
        addSnapshot,
        updateSnapshot,
        deleteSnapshot,
        addCaixinhaLog,
        updateCaixinhaLog,
        deleteCaixinhaLog,
        investmentAssets,
        activeInvestmentAssets,
        trades,
        earnings,
        corporateActions,
        conversions,
        loadingInvestments,
        addInvestmentAsset,
        updateInvestmentAsset,
        deleteInvestmentAsset,
        addTrade,
        updateTrade,
        deleteTrade,
        addEarning,
        updateEarning,
        deleteEarning,
        addCorporateAction,
        deleteCorporateAction,
        addConversion,
        deleteConversion,
        importInvestments,
        orphanRecords,
        cleanupOrphanRecords,
        mismatchedEarnings,
        fixEarningKinds,
        refreshQuotes,
        refreshingQuotes,
        quotesError,
        positions,
        portfolioSummary,
        monthEarnings,
        caixinhasTotal,
        bitcoinTotal,
        investmentsTotal,
        netWorth,
        totalIncome,
        totalMoney,
        totalExpenses,
        balance,
        bitcoinPrice,
        loadingBitcoinPrice,
        bitcoinPriceError,
        refreshData,
        viewingAs,
        setViewingAs,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData deve ser usado dentro de um AppDataProvider');
  }
  return context;
}
